/*
 * Copyright ©2020. Open Digital Solutions, Novi Sad. Sva prava zadržana.
 * Pravo da se koristi, kopira, modifikuje i distribuira ovaj softver i njegova dokumentacija
 * u bilo koje svrhe, bez naknade ili bez potpisanog sporazuma sa vlasnikom softvera, nije dozvoljeno.
 */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SubjectsComponent } from '../../subjects/subjects.component';
import { LocalStorageService } from '../../local-storage.service';
import { DutyProceedingsInitiationService } from 'src/services/duty-proceedings-initiation.service';
import { CodebookService } from 'src/services/codebook.service';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { autoSelect } from 'src/app/utils/autoSelect';

enum AutoCompleteFilterEnum {
   CASE_CLASSIFICATION = 'caseClassification',
}
@Component({
   selector: 'app-duty-proceedings-initiation',
   templateUrl: './duty-proceedings-initiation.component.html',
   styleUrls: ['./duty-proceedings-initiation.component.scss'],
})
export class DutyProceedingsInitiationComponent implements OnInit {
   dutyProceedingsForm: FormGroup;
   private subjectId: number | null = null;

   caseClassifications: any[] = [];
   filteredCaseClassifications: Observable<any[]>;
   private professionalWorkerFirstName: string = '';
   private professionalWorkerLastName: string = '';
   loggedIn: any;

   constructor(
      private formBuilder: FormBuilder,
      private dialog: MatDialog,
      private localStorageService: LocalStorageService,
      private dpiService: DutyProceedingsInitiationService,
      private codebookService: CodebookService,
   ) {
      this.initForm();

      this.loggedIn = JSON.parse(this.localStorageService.get('loggedUser'));
      const fullName = this.loggedIn.firstName + ' ' + this.loggedIn.lastName;
      this.professionalWorkerFirstName = this.loggedIn?.firstName || '';
      this.professionalWorkerLastName = this.loggedIn?.lastName || '';
      this.dutyProceedingsForm.patchValue({ professionalWorker: fullName });

      this.codebookService.getActiveSocialCaseClassificationCodebook().subscribe(result => {
         this.caseClassifications = result || [];

         this.filteredCaseClassifications = this.dutyProceedingsForm.controls.caseClassification.valueChanges.pipe(
            startWith(''),
            map(value => (typeof value === 'string' ? value : value.title)),
            map(title => (title ? this._filter(title, AutoCompleteFilterEnum.CASE_CLASSIFICATION) : this.caseClassifications.slice())),
         );
      });
   }

   ngOnInit(): void {}

   private initForm(): void {
      this.dutyProceedingsForm = this.formBuilder.group({
         firstName: ['', Validators.required],
         lastName: ['', Validators.required],
         registrationNumber: ['', Validators.required],
         jmbg: ['', Validators.required],
         caseClassification: ['', Validators.required],
         reasonForProcedure: ['', Validators.required],
         date: [new Date(), Validators.required],
         professionalWorker: ['', Validators.required],
      });
   }

   private _filter(value: string, what: AutoCompleteFilterEnum): any[] {
      const filterValue = value.toLowerCase();
      switch (what) {
         case AutoCompleteFilterEnum.CASE_CLASSIFICATION:
            return this.caseClassifications.filter(option => option.title.toLowerCase().includes(filterValue) || option.code.toLowerCase().includes(filterValue));

         default:
            break;
      }
   }

   displayClassification = (item: any) => (item && item.title ? `${item.code} | ${item.title}` : item);
   checkClasificationCase(fast: boolean) {
      if (fast === false) {
         setTimeout(() => {
            this.checkClassification();
         }, 300);
      } else {
         this.checkClassification();
      }
   }

   checkClassification() {
      const temp = this.dutyProceedingsForm;
      const caseClassifications = this.caseClassifications;
      if (temp.value.caseClassification.code === undefined || temp.value.caseClassification.code === null) {
         const founded = caseClassifications.filter(
            caseClass => caseClass.title.toLowerCase().includes(temp.value.caseClassification.toLowerCase()) || caseClass.code.toLowerCase().includes(temp.value.caseClassification.toLowerCase()),
         );
         if (founded.length === 0) {
            temp.controls.caseClassification.setErrors({ notValid: true });
            return;
         } else {
            temp.controls.caseClassification.setValue(founded[0]);
            temp.controls.caseClassification.setErrors(null);
         }
      }
   }

   private filterCaseClassification(value: any): any[] {
      const str = typeof value === 'string' ? value.toLowerCase() : (value?.title || '').toLowerCase();
      return this.caseClassifications.filter(opt => opt?.title?.toLowerCase().includes(str) || opt?.code?.toLowerCase().includes(str));
   }

   importSubjectData(): void {
      const dialogRef = this.dialog.open(SubjectsComponent, {
         panelClass: 'overlay-panel',
         width: '1200px',
         data: {
            origin: 'duty-proceedings-initiation',
         },
      });

      dialogRef.afterClosed().subscribe(result => {
         if (result !== undefined) {
            const obj = JSON.parse(JSON.stringify(result));
            this.subjectId = obj?.data?.subjectIdentity?.subjectId ?? null;
            const {
               data: { firstName, lastName, registrationId, jmbg },
            } = obj;

            this.dutyProceedingsForm.patchValue({
               firstName,
               lastName,
               registrationNumber: registrationId,
               jmbg,
            });
         }
      });
   }

   private createDto(): any {
      const selectedClass = this.dutyProceedingsForm.value.caseClassification;
      const caseClassCode = selectedClass && selectedClass.code ? selectedClass.code : '';
      const caseClassTitle = selectedClass && selectedClass.title ? selectedClass.title : '';
      const dto: any = {
         center: this.loggedIn?.center || '',
         firstName: this.dutyProceedingsForm.value.firstName || '',
         lastName: this.dutyProceedingsForm.value.lastName || '',
         registrationNumber: this.dutyProceedingsForm.value.registrationNumber || '',
         jmbg: this.dutyProceedingsForm.value.jmbg || '',
         caseClassCode,
         caseClassTitle,
         reasonForProcedure: this.dutyProceedingsForm.value.reasonForProcedure || '',
         date: this.dutyProceedingsForm.value.date || new Date(),
         professionalWorkerFirstName: this.professionalWorkerFirstName,
         professionalWorkerLastName: this.professionalWorkerLastName,
      };

      if (this.subjectId !== null) {
         dto.subjectId = this.subjectId;
      }

      return dto;
   }

   saveDecision(): void {
      if (this.dutyProceedingsForm.valid) {
         const dto = this.createDto();
         this.dpiService.createDPIRequest(dto);
      } else {
         this.dutyProceedingsForm.markAllAsTouched();
      }
   }

   printPreview(): void {
      if (this.dutyProceedingsForm.valid) {
         const dto = this.createDto();
         this.dpiService.printPreview(dto);
      }
   }
}
